import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, split, Observable, type DocumentNode } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { authService } from '@features/auth/services/authService';
import { refreshToken } from './refreshToken';
import { navigateToLogin } from '@utils/navigateToLogin';
import { typePolicies } from './cacheConfig';
import { CacheCleanupService, PerformanceMonitor } from '../services/cacheCleanupService';
import { TTLManager } from '../services/ttlManager';
import siteConfig from '@config';
import { init, type GqlExecutor } from '@hopdrive/sdk-core';
import { setAmountDueExecutor } from '@features/invoices/services/amountDueExecutor';

// TTL configuration for different data types
const TTL_CONFIG = {
  moves: 1 * 60 * 60 * 1000, // 1 hour
  drivers: 1 * 24 * 60 * 60 * 1000, // 1 day
  customers: 2 * 60 * 60 * 1000, // 2 hours
  locations: 1 * 24 * 60 * 60 * 1000, // 1 day
  lanes: 1 * 24 * 60 * 60 * 1000, // 1 day
  regions: 1 * 24 * 60 * 60 * 1000, // 1 day
  hangtags: 1 * 24 * 60 * 60 * 1000, // 1 day
  workflows: 7 * 24 * 60 * 60 * 1000, // 7 days
  default: 30 * 60 * 1000, // 30 minutes default
};

// Global TTL store for tracking cache entry expiration times
export const ttlStore = new Map<string, number>();

// Initialize TTL manager for async processing
const ttlManager = TTLManager.getInstance(ttlStore);

// TTL helper functions
export function setTTL(cacheId: string, ttl: number): void {
  // Queue for async processing instead of direct update
  ttlManager.queueTTLUpdate(cacheId, ttl);
}

export function isExpired(cacheId: string): boolean {
  const expiry = ttlStore.get(cacheId);
  return expiry ? Date.now() > expiry : false;
}

export function getTTLForType(typename: string): number {
  const type = typename.toLowerCase();
  return TTL_CONFIG[type as keyof typeof TTL_CONFIG] || TTL_CONFIG.default;
}

const httpLink = createHttpLink({
  uri: siteConfig.graphqlHttpUrl,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: siteConfig.graphqlWsUrl,
    connectionParams: async () => {
      return {
        headers: {
          authorization: `Bearer ${await authService.getAccessTokenWithAutoRefresh()}`,
          'hasura-client-name': siteConfig.graphqlClientName,
          // Request WebSocket compression via headers
          'Sec-WebSocket-Extensions': 'permessage-deflate; client_max_window_bits',
        },
      };
    },
  })
);

const authLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${await authService.getAccessTokenWithAutoRefresh()}`,
      'hasura-client-name': siteConfig.graphqlClientName,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === 'UNAUTHENTICATED' || err.message.includes('JWTExpired') || err.extensions?.code === 'invalid-jwt') {
        // Return an Observable that handles token refresh and retry
        return new Observable(observer => {
          // Log for debugging in development
          if (process.env.NODE_ENV === 'development') {
            console.log('[Apollo] JWT expired, attempting to refresh token...');
          }

          refreshToken()
            .then(async () => {
              // Get fresh token after refresh
              const newToken = await authService.getAccessTokenWithAutoRefresh();

              // Update the operation context with new token
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: `Bearer ${newToken}`,
                },
              });

              if (process.env.NODE_ENV === 'development') {
                console.log('[Apollo] Token refreshed successfully, retrying operation...');
              }

              // Retry the operation with new token
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };
              forward(operation).subscribe(subscriber);
            })
            .catch(error => {
              console.error('[Apollo] Failed to refresh token:', error);
              observer.error(error);
              navigateToLogin();
            });
        });
      }
    }
  }

  if (networkError) {
    console.error(`[Network Error]: ${networkError.message}`);
  }

  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    navigateToLogin();
  }
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const link = ApolloLink.from([errorLink, authLink, splitLink]);

// Create cache with TTL-aware type policies
const cache = new InMemoryCache({
  typePolicies: {
    ...typePolicies,
    // TTL support for individual entities only (not Query fields)
    Move: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Move:${id}`;
          setTTL(cacheId, TTL_CONFIG.moves);
        }
        return mergeObjects(existing, incoming);
      },
    },
    Driver: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Driver:${id}`;
          setTTL(cacheId, TTL_CONFIG.drivers);
        }
        return mergeObjects(existing, incoming);
      },
    },
    Customer: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Customer:${id}`;
          setTTL(cacheId, TTL_CONFIG.customers);
        }
        return mergeObjects(existing, incoming);
      },
    },
    Location: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Location:${id}`;
          setTTL(cacheId, TTL_CONFIG.locations);
        }
        return mergeObjects(existing, incoming);
      },
    },
    Lane: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Lane:${id}`;
          setTTL(cacheId, TTL_CONFIG.lanes);
        }
        return mergeObjects(existing, incoming);
      },
    },
    Region: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Region:${id}`;
          setTTL(cacheId, TTL_CONFIG.regions);
        }
        return mergeObjects(existing, incoming);
      },
    },
    HangTag: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `HangTag:${id}`;
          setTTL(cacheId, TTL_CONFIG.hangtags);
        }
        return mergeObjects(existing, incoming);
      },
    },
    Workflow: {
      keyFields: ['id'],
      merge: (existing, incoming, { mergeObjects, readField }) => {
        const id = readField('id', incoming);
        if (id) {
          const cacheId = `Workflow:${id}`;
          setTTL(cacheId, TTL_CONFIG.workflows);
        }
        return mergeObjects(existing, incoming);
      },
    },
  },
});

const client = new ApolloClient({
  link,
  cache,
});

/**
 * App-owned Apollo executor: a thin adapter over this app's ApolloClient,
 * registered as sdk-core's default so every sdk-* package and the colocated
 * generated wrappers run through it. Reads use network-only (imperative callers
 * want fresh data); React hooks keep Apollo's cache-first default.
 */
export const gqlExecutor: GqlExecutor = {
  query: async (document, variables) =>
    (
      await client.query({
        query: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
        fetchPolicy: 'network-only',
      })
    ).data,
  mutate: async (document, variables) =>
    (
      await client.mutate({
        mutation: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
      })
    ).data,
};
init({ executor: gqlExecutor });

// The invoice screens' Amount Due reads skip the cache: they are used once, and
// writing each answer into the cache made Apollo re-read every watched query it
// touched, about 0.4 s a read with a large invoice on screen.
setAmountDueExecutor({
  query: async (document, variables) =>
    (
      await client.query({
        query: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
        fetchPolicy: 'no-cache',
      })
    ).data,
  mutate: gqlExecutor.mutate,
});

// Initialize cache cleanup service with environment-specific configuration
const cleanupConfig = {
  intervalMs: process.env.NODE_ENV === 'production' ? 5 * 60 * 1000 : 2 * 60 * 1000, // 5min prod, 2min dev
  maxCleanupTime: process.env.NODE_ENV === 'production' ? 250 : 1000, // Conservative in prod
  batchSize: process.env.NODE_ENV === 'production' ? 20 : 50, // Smaller batches in prod
  throttleMs: process.env.NODE_ENV === 'production' ? 25 : 10, // More throttling in prod
  enabled: true,
};

export const cleanupService = CacheCleanupService.getInstance(cache, ttlStore, cleanupConfig);

// Start the cleanup service automatically
cleanupService.start();

// Development-only performance monitoring
if (process.env.NODE_ENV === 'development') {
  // Log performance stats periodically
  const perfInterval = setInterval(() => {
    const stats = PerformanceMonitor.getAllStats();
    if (Object.keys(stats).length > 0) {
      //console.table(stats);
    }
  }, 30000); // Every 30 seconds in development

  // Clean up on window unload
  window.addEventListener('beforeunload', () => {
    clearInterval(perfInterval);
  });
}

// Clean up TTL manager on window unload
window.addEventListener('beforeunload', async () => {
  // Flush any pending TTL updates
  await ttlManager.flush();
});

//console.log('[apollo] client instance', client);

export default client;
export { ttlManager };
